import dayjsTzPlugin from "dayjs/plugin/timezone";
import dayjsUtcPlugin from "dayjs/plugin/utc";
import dayjs, { type Dayjs } from "dayjs";
import { useRef, useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Popup } from "components/popup";
import { useDispatch } from "hooks/use-dispatch";
import { useSelector } from "hooks/use-selector";
import { setInitialDepositDate } from "store/quote";
import { selectResidency, selectTripCost } from "store/quote/selectors";
import {
  mixpanelTrack,
  mixpanelTrackInteraction,
} from "utils/tracking/mixpanel";
import type { ValidationResult } from "utils/validation";
import { trackCancelForAnyReasonDepositDate } from "../../../tracking";
import { useCoverage } from "../../../utils";
import { Item, type ItemProps } from "../item";
import { PopupButton } from "../popup-button";
import { PopupHeader } from "../popup-header";
import { PopupSection } from "../popup-section";
import { type CfarDateInputProps, CfarDateInput } from "./date-input";
import {
  CANCEL_FOR_ANY_REASON_DAYS_LIMIT_MISSOURI,
  CANCEL_FOR_ANY_REASON_DAYS_LIMIT,
} from "settings";
import { PopupBody, DateInputContainer, SubTitle } from "./styles";

dayjs.extend(dayjsTzPlugin);
dayjs.extend(dayjsUtcPlugin);

export function CancelForAnyReason({
  coverageType,
  price,
  ...props
}: ItemProps) {
  const dispatch = useDispatch();
  const residency = useSelector(selectResidency);
  const tripCost = useSelector(selectTripCost);
  const {
    coverage,
    enable: enableCoverage,
    disable: disableCoverage,
  } = useCoverage(coverageType);

  const { t } = useTranslation("screens", {
    keyPrefix: "offer.addOns.cancelForAnyReason",
  });
  const inputRef = useRef<HTMLDivElement>(null);

  const [showPopup, setShowPopup] = useState(false);
  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const [depositDate, setDepositDate] = useState({
    validation: null as ValidationResult | null,
    value: null as Dayjs | null,
  });

  const isValid = depositDate.validation?.status === "valid" && isEligible;

  useEffect(() => {
    if (tripCost === 0) {
      disableCoverage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripCost]);

  function closePopup() {
    setShowPopup(false);

    if (isEligible && depositDate.value) {
      trackCancelForAnyReasonDepositDate(
        "clicked_add_cfar",
        depositDate.value.toDate(),
      );
    } else {
      mixpanelTrack("closed_cfar_popup");
    }
  }

  function openPopup() {
    if (coverage?.isSelected) {
      resetCoverage();
    } else {
      setShowPopup(true);
      mixpanelTrack("cfar_popup_opened");
    }
  }

  function toggleCFARCoverageStatus() {
    if (!isEligible && !coverage?.isSelected) {
      inputRef.current?.querySelector("input")?.focus();
    } else if (coverage?.isSelected) {
      resetCoverage();
    } else {
      addCFAR();
    }
  }

  function resetCoverage() {
    setDepositDate({ validation: null, value: null });
    setIsEligible(null);
    disableCoverage();
    mixpanelTrackInteraction("Click disable CFAR", {
      component_name: "CFAR",
      interaction_type: "button",
      interaction_text: "Add CFAR",
      interaction_region: props.inlineActionInfo
        ? "inline add on option"
        : "in CFAR popup",
      name_of_page: "offer",
    });
  }

  function addCFAR() {
    if (isEligible && depositDate.value) {
      const isoDate = depositDate.value.toISOString();
      dispatch(setInitialDepositDate(isoDate));
      enableCoverage();
      mixpanelTrackInteraction("Click enable CFAR", {
        component_name: "CFAR",
        interaction_type: "button",
        interaction_text: t("popup.ctaAdd"),
        interaction_region: props.inlineActionInfo
          ? "inline add on option"
          : "in CFAR popup",
        name_of_page: "offer",
      });
    }
  }

  function validateDateInput(value: Dayjs | null) {
    setIsEligible(null);

    if (value === null) {
      return true;
    } else if (!value.isValid()) {
      return t("popup.errorMessages.invalid");
    }

    const today = dayjs.utc().startOf("day");
    const daysFromDeposit = today.diff(value, "days");

    const daysLimit =
      residency?.areaLevel1 === "MO"
        ? CANCEL_FOR_ANY_REASON_DAYS_LIMIT_MISSOURI
        : CANCEL_FOR_ANY_REASON_DAYS_LIMIT;

    if (daysFromDeposit > daysLimit || daysFromDeposit < 0) {
      setIsEligible(false);
      trackCancelForAnyReasonDepositDate("saw_cfar_error", value.toDate());
    } else {
      setIsEligible(true);
    }

    return true;
  }

  function onChange(value: Dayjs | null) {
    setDepositDate({
      ...depositDate,
      value: value?.tz("UTC", true) ?? null,
    });
  }

  function onValidation(validation: ValidationResult | null) {
    if (validation) {
      setDepositDate({
        ...depositDate,
        validation,
      });
    }
  }

  const cfarProps: CfarDateInputProps = {
    value: depositDate.value,
    isEligible,
    onValidation,
    onChange,
    validate: validateDateInput,
    InputProps: { ref: inputRef },
  };

  return (
    <>
      <Item
        {...props}
        CtaButtonProps={{
          onClick: props.inlineActionInfo
            ? toggleCFARCoverageStatus
            : openPopup,
        }}
        isDisabled={tripCost === 0}
        coverageType={coverageType}
        price={price}
      >
        {props.inlineActionInfo && !coverage?.isSelected && (
          <>
            <SubTitle>{t("subtitle")}</SubTitle>
            <DateInputContainer>
              <CfarDateInput {...cfarProps} />
            </DateInputContainer>
          </>
        )}
      </Item>

      {!props.inlineActionInfo && (
        <Popup onClose={closePopup} open={showPopup}>
          <PopupHeader onClose={closePopup} title={t("popup.title")} screen />

          <PopupBody screen>
            <PopupSection as="p">
              <Trans
                i18nKey={
                  residency?.areaLevel1 === "MO"
                    ? "missouri.popup.description"
                    : "popup.description"
                }
                t={t}
              />
            </PopupSection>

            <PopupSection>
              <CfarDateInput {...cfarProps} />
            </PopupSection>

            <PopupButton
              showArrow={!!isValid}
              disabled={
                !depositDate.value || depositDate.validation?.status !== "valid"
              }
              onClick={() => {
                addCFAR();
                closePopup();
              }}
            >
              {isValid ? t("popup.ctaAdd") : t("popup.ctaClose")}
            </PopupButton>
          </PopupBody>
        </Popup>
      )}
    </>
  );
}
